/**
 * The sample's safety register: hazards, safety reports and security
 * incidents, kept in memory and worked by the same rules the platform applies.
 *
 * Seeded in the platform's own shapes from the seven hazards and three reports
 * the old safety board has always shown — never by importing the board's
 * tables, which would put the demo domain in the entry chunk. References keep
 * the board's ids (HZ-001, SR-002) so a row the board drew is the row this
 * register holds; everything seeded is marked demonstration.
 *
 * The rules are the platform's, so the demo never shows something the
 * platform would refuse: references are allocated here, a report closes only
 * with an outcome, an anonymous report keeps no name, an intolerable hazard
 * closes only with an accepted risk, and an incident moves only along the
 * pack's transitions.
 *
 * `mockSafetyVersion()` counts the writes made in this page. The board keeps
 * its sample exactly as seeded until somebody writes, and reads this register
 * from then on (SafetyFeed).
 */
import { DataSourceError } from "../api/dataSource";
import {
  bandOf,
  CONFIDENTIALITIES,
  CONTROL_TYPES,
  INCIDENT_TRANSITIONS,
  LIVE_HAZARD,
  REPORT_STATUSES,
  reportTitle,
  type AssessedHazard,
  type AssessHazardInput,
  type AssessmentKind,
  type CloseHazardInput,
  type Confidentiality,
  type ControlType,
  type FiledReport,
  type FileReportInput,
  type Hazard,
  type HazardActionInput,
  type HazardDetail,
  type HazardPage,
  type HazardStatus,
  type IncidentCategory,
  type IncidentStatus,
  type RaisedIncident,
  type RaiseHazardInput,
  type RaiseIncidentInput,
  type ReportKind,
  type ReportStatus,
  type RiskVerdict,
  type SafetyMatrix,
  type SafetyReport,
  type SafetyReportPage,
  type SafetyVocabulary,
  type SecurityIncident,
  type SecurityIncidentPage,
} from "../domain/safety";

/* ── writes in this page ────────────────────────────────────────────────── */

let version = 0;
const listeners = new Set<() => void>();

/** How many safety writes the sample has taken in this page. */
export const mockSafetyVersion = (): number => version;

export function subscribeMockSafety(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function wrote(): void {
  version += 1;
  for (const fn of listeners) fn();
}

/** For tests: forget that anything was written. */
export function resetMockSafetyVersion(): void {
  version = 0;
}

/* ── the pack's vocabulary, as the platform publishes it ─────────────────── */

const cells: Record<string, "intolerable" | "tolerable" | "acceptable"> = {};
for (const c of ["5A", "5B", "5C", "4A", "4B", "3A"]) cells[c] = "intolerable";
for (const c of ["5D", "5E", "4C", "4D", "3B", "3C", "2A", "2B", "2C", "1A"]) cells[c] = "tolerable";
for (const c of ["4E", "3D", "3E", "2D", "2E", "1B", "1C", "1D", "1E"]) cells[c] = "acceptable";

export const MOCK_MATRIX: SafetyMatrix = {
  key: "icao_5x5",
  version: "2024.1",
  name: "ICAO 5×5 safety risk matrix",
  likelihood: [
    { code: "5", name: "Frequent", rank: 5 },
    { code: "4", name: "Occasional", rank: 4 },
    { code: "3", name: "Remote", rank: 3 },
    { code: "2", name: "Improbable", rank: 2 },
    { code: "1", name: "Extremely improbable", rank: 1 },
  ],
  severity: [
    { code: "A", name: "Catastrophic", rank: 5 },
    { code: "B", name: "Hazardous", rank: 4 },
    { code: "C", name: "Major", rank: 3 },
    { code: "D", name: "Minor", rank: 2 },
    { code: "E", name: "Negligible", rank: 1 },
  ],
  cells,
  reviewMonths: { intolerable: 1, tolerable: 3, acceptable: 12 },
};

const CATEGORIES: [string, string][] = [
  ["wildlife", "Wildlife hazard"],
  ["foreign_object_debris", "Foreign object debris"],
  ["incursion_risk", "Incursion risk"],
  ["surface_condition", "Surface condition"],
  ["visual_aids", "Visual aids and lighting"],
  ["obstacle", "Obstacle and airspace protection"],
  ["apron_operations", "Apron and ground handling"],
  ["works_in_progress", "Works in progress"],
  ["low_visibility", "Low visibility and adverse weather"],
  ["fuel_and_dangerous_goods", "Fuel and dangerous goods"],
  ["terminal_environment", "Terminal environment"],
  ["landside_traffic", "Landside traffic"],
  ["emergency_response", "Emergency response capability"],
  ["organisational", "Organisational and procedural"],
];

export const MOCK_SAFETY_VOCABULARY: SafetyVocabulary = {
  matrices: [MOCK_MATRIX],
  categories: CATEGORIES.map(([key, name]) => ({ key, name, defaultMatrix: "icao_5x5" })),
  issues: [],
};

const GACA_SEC = "GACA Aviation Security";
export const MOCK_INCIDENT_CATEGORIES: IncidentCategory[] = [
  { key: "sra_breach", name: "Security Restricted Area breach", defaultSeverity: 5, defaultClassification: "restricted", reportable: true, reportTo: GACA_SEC, reportWithinHours: 2 },
  { key: "perimeter_breach", name: "Perimeter breach", defaultSeverity: 5, defaultClassification: "restricted", reportable: true, reportTo: GACA_SEC, reportWithinHours: 2 },
  { key: "unauthorised_airside_entry", name: "Unauthorised airside entry", defaultSeverity: 4, defaultClassification: "restricted", reportable: true, reportTo: GACA_SEC, reportWithinHours: 24 },
  { key: "unattended_item", name: "Unattended item", defaultSeverity: 3, defaultClassification: "internal", reportable: false },
  { key: "prohibited_article", name: "Prohibited article detected", defaultSeverity: 4, defaultClassification: "restricted", reportable: true, reportTo: GACA_SEC, reportWithinHours: 24 },
  { key: "screening_failure", name: "Screening process failure", defaultSeverity: 4, defaultClassification: "restricted", reportable: true, reportTo: GACA_SEC, reportWithinHours: 24 },
  { key: "credential_misuse", name: "Credential misuse or loss", defaultSeverity: 3, defaultClassification: "internal", reportable: false },
  { key: "detection_system_outage", name: "Detection system outage", defaultSeverity: 3, defaultClassification: "internal", reportable: false },
  { key: "airside_vehicle_incursion", name: "Vehicle incursion onto the manoeuvring area", defaultSeverity: 5, defaultClassification: "internal", reportable: true, reportTo: "GACA Safety", reportWithinHours: 24 },
  { key: "security_drill", name: "Security exercise or drill", defaultSeverity: 1, defaultClassification: "internal", reportable: false },
];

/* ── stored shapes ──────────────────────────────────────────────────────── */

interface StoredAssessment {
  kind: AssessmentKind;
  likelihood: string;
  severity: string;
  at: string;
  rationale?: string;
}

interface StoredAction {
  seq: number;
  description: string;
  controlType: ControlType;
  dueDate?: string;
  status: "open" | "in_progress" | "done" | "cancelled";
  completedAt?: string;
}

interface StoredHazard {
  id: string;
  reference: string;
  category: string;
  title: string;
  description?: string;
  status: HazardStatus;
  origin: string;
  siteId?: string;
  area?: string;
  ownerLabel?: string;
  identifiedAt: string;
  reviewDue?: string;
  closedAt?: string;
  closureNote?: string;
  demo: boolean;
  assessments: StoredAssessment[];
  actions: StoredAction[];
  acceptances: { acceptedAt: string; rationale: string; riskIndex?: string; tolerability?: string }[];
}

interface StoredReport {
  id: string;
  reference: string;
  confidentiality: Confidentiality;
  title: string;
  narrative: string;
  category?: string;
  siteId?: string;
  reportedAt: string;
  status: ReportStatus;
  hazardId?: string;
  outcome?: string;
  closedAt?: string;
  attrs: Record<string, unknown>;
  /** null when anonymous — the name is never stored, not hidden */
  reporter: string | null;
  demo: boolean;
}

/* ── the seed: the board's own sample, in the platform's shapes ─────────── */

const YEAR = new Date().getUTCFullYear();
const day = (d: string) => `${d}T08:00:00Z`;

interface HazardSeed {
  ref: string;
  category: string;
  title: string;
  area: string;
  owner: string;
  raised: string;
  initial: [string, string];
  residual: [string, string];
  status: HazardStatus;
  /** the controls the board lists; the last `open` of them are still being put in place */
  controls: string[];
  open: number;
  extra?: string[];
  note: string;
}

const HAZARD_SEEDS: HazardSeed[] = [
  { ref: "HZ-001", category: "surface_condition", title: "Rubber accumulation reducing friction in the touchdown zones", area: "Runway 17/35", owner: "Airside operations", raised: "2026-06-02", initial: ["4", "B"], residual: ["2", "B"], status: "monitored", controls: ["Quarterly friction survey", "Removal at the planning level rather than the minimum", "Verification survey after every removal"], open: 1, note: "Two bands below the planning level in the last survey; one unexplained mid-runway." },
  { ref: "HZ-002", category: "incursion_risk", title: "Runway incursion at the hold points during low visibility", area: "Movement area", owner: "Airside operations", raised: "2025-11-18", initial: ["3", "A"], residual: ["2", "A"], status: "monitored", controls: ["Stop bars serviceable and used", "Driver permit scheme", "LVP procedures"], open: 0, note: "No incursions recorded this year. Zero is not evidence the control is working — the stop bar serviceability is." },
  { ref: "HZ-003", category: "foreign_object_debris", title: "Foreign object debris from construction traffic", area: "Apron and taxiways", owner: "Airside operations", raised: "2026-05-11", initial: ["4", "C"], residual: ["3", "C"], status: "mitigating", controls: ["Sweeping before and after each construction shift", "FOD checks at every access gate"], open: 2, note: "Apron expansion has increased vehicle movements through the eastern gate." },
  { ref: "HZ-004", category: "terminal_environment", title: "Lift and escalator brake failure in the terminal", area: "Terminal", owner: "Maintenance", raised: "2026-02-11", initial: ["3", "C"], residual: ["2", "D"], status: "monitored", controls: ["Statutory examination", "Two units withdrawn pending retest"], open: 1, note: "Raised from the failed statutory examination." },
  { ref: "HZ-005", category: "terminal_environment", title: "Loss of standby power during a terminal outage", area: "Terminal and airfield", owner: "Maintenance", raised: "2026-03-20", initial: ["2", "B"], residual: ["2", "C"], status: "monitored", controls: ["Monthly generator test", "UPS on critical systems", "Load bank test annually"], open: 1, note: "Generator testing interval has slipped past thirty days." },
  { ref: "HZ-006", category: "terminal_environment", title: "Baggage handling stoppage at peak", area: "Baggage hall", owner: "Terminal operations", raised: "2026-07-15", initial: ["4", "D"], residual: ["3", "D"], status: "mitigating", controls: ["Fallback and recirculation proven", "Manual sortation plan"], open: 2, note: "Line 3 trial reached 1,180 bags an hour against 1,500 specified." },
  { ref: "HZ-007", category: "wildlife", title: "Wildlife strike on approach", area: "Aerodrome and surrounds", owner: "Airside operations", raised: "2026-01-08", initial: ["3", "B"], residual: ["3", "C"], status: "assessed", controls: ["Habitat management", "Dispersal patrols"], open: 2, extra: ["Habitat survey of the surrounds"], note: "Eleven strikes in twelve months against a target of eight; no habitat survey on file." },
];

function seedHazards(): StoredHazard[] {
  return HAZARD_SEEDS.map((s, i) => {
    const firstOpen = s.controls.length - s.open;
    const actions: StoredAction[] = [...s.controls, ...(s.extra ?? [])].map((description, k) => {
      const open = k >= firstOpen;
      return {
        seq: k + 1,
        description,
        controlType: "administrative",
        status: open ? "open" : "done",
        ...(open ? {} : { completedAt: day(s.raised) }),
      };
    });
    return {
      id: `mock-hz-${String(i + 1).padStart(3, "0")}`,
      reference: s.ref,
      category: s.category,
      title: s.title,
      description: s.note,
      status: s.status,
      origin: "report",
      area: s.area,
      ownerLabel: s.owner,
      identifiedAt: day(s.raised),
      demo: true,
      assessments: [
        { kind: "initial", likelihood: s.initial[0], severity: s.initial[1], at: day(s.raised) },
        { kind: "residual", likelihood: s.residual[0], severity: s.residual[1], at: day(s.raised) },
      ],
      actions,
      acceptances: [],
    };
  });
}

function seedReports(): StoredReport[] {
  const r = (
    n: number,
    kind: ReportKind,
    conf: Confidentiality,
    reporter: string | null,
    at: string,
    where: string,
    narrative: string,
    status: ReportStatus,
    hazard?: string,
    outcome?: string,
  ): StoredReport => ({
    id: `mock-sr-${String(n).padStart(3, "0")}`,
    reference: `SR-${String(n).padStart(3, "0")}`,
    confidentiality: conf,
    title: reportTitle(kind, conf, where),
    narrative,
    reportedAt: day(at),
    status,
    hazardId: hazard,
    outcome,
    closedAt: status === "closed" ? day(at) : undefined,
    attrs: { kind, where },
    reporter,
    demo: true,
  });
  return [
    r(1, "hazard", "open", "Ramp agent", "2026-08-14", "Stand 12", "Ground power cable left across the walkway after departure. Someone will trip on it at night.", "received"),
    r(2, "occurrence", "confidential", null, "2026-08-09", "Taxiway J1", "Held short of the runway with the markings barely visible in the rain. I was not certain I was at the right hold point.", "triaged", "mock-hz-002"),
    r(3, "suggestion", "open", "Duty engineer", "2026-07-28", "Baggage hall", "The manual sortation plan has never been exercised at peak. Worth running it during a quiet period first.", "closed", "mock-hz-006", "Accepted: the plan will be exercised in a quiet period first."),
  ];
}

/* ── the register ───────────────────────────────────────────────────────── */

const refuse = (message: string, status = 400) => new DataSourceError(message, { status });
const nowIso = () => new Date().toISOString();

function verdict(a: StoredAssessment | undefined): RiskVerdict {
  if (!a) {
    return {
      state: "never_assessed",
      tolerability: null,
      why: "no assessment has ever been recorded against this hazard. Never assessed is not acceptable",
    };
  }
  const tolerability = bandOf(MOCK_MATRIX, a.likelihood, a.severity);
  return {
    state: tolerability === "unknown" ? "unclassified" : "assessed",
    tolerability,
    riskIndex: `${a.likelihood}${a.severity}`,
    likelihood: a.likelihood,
    severity: a.severity,
    kind: a.kind,
    matrix: `${MOCK_MATRIX.key}@${MOCK_MATRIX.version}`,
    assessedAt: a.at,
    why: null,
  };
}

function nextRef(prefix: string, taken: string[]): string {
  const re = new RegExp(`^${prefix}-${YEAR}-(\\d+)$`);
  const n = taken.reduce((m, r) => Math.max(m, Number(re.exec(r)?.[1] ?? 0)), 0);
  return `${prefix}-${YEAR}-${String(n + 1).padStart(4, "0")}`;
}

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

export class MockSafety {
  private hazards: StoredHazard[] = seedHazards();
  private reports: StoredReport[] = seedReports();
  private incidents: (SecurityIncident & { siteId: string; links: { kind: string; id: string }[] })[] = [];
  private replays = new Map<string, unknown>();
  private readonly principal: string;

  constructor(principal: string) {
    this.principal = principal;
  }

  /* idempotency: the same key answers with the first result */
  private once<T>(key: string | undefined, make: () => T): T {
    if (key && this.replays.has(key)) return { ...(this.replays.get(key) as object), replayed: true } as T;
    const out = make();
    if (key) this.replays.set(key, out);
    wrote();
    return out;
  }

  private hazard(id: string): StoredHazard {
    const h = this.hazards.find((x) => x.id === id);
    if (!h) throw refuse("no such hazard", 404);
    return h;
  }

  private live(id: string): StoredHazard {
    const h = this.hazards.find((x) => x.id === id);
    if (!h) throw refuse("no such hazard on the register");
    if (!LIVE_HAZARD.includes(h.status)) {
      throw refuse(`hazard ${h.reference} is ${h.status}. File the report on its own; the safety office decides whether it opens anything again`);
    }
    return h;
  }

  private present(h: StoredHazard): Hazard {
    const last = h.assessments[h.assessments.length - 1];
    const initial = [...h.assessments].reverse().find((a) => a.kind === "initial");
    const open = h.actions.filter((a) => a.status === "open" || a.status === "in_progress");
    const closed = h.status === "closed" || h.status === "rejected";
    const due = h.reviewDue;
    const days = due ? Math.round((Date.parse(due) - Date.parse(today())) / 86_400_000) : 0;
    return {
      id: h.id,
      reference: h.reference,
      category: h.category,
      title: h.title,
      description: h.description,
      status: h.status,
      origin: h.origin,
      siteId: h.siteId,
      area: h.area,
      ownerLabel: h.ownerLabel,
      identifiedAt: h.identifiedAt,
      reviewDue: due,
      closedAt: h.closedAt,
      openActions: open.length,
      overdueActions: open.filter((a) => a.dueDate && a.dueDate < today()).length,
      riskAccepted: h.acceptances.length > 0,
      demo: h.demo,
      risk: verdict(last),
      initialRisk: verdict(initial),
      controls: h.actions.filter((a) => a.status !== "cancelled").map((a) => a.description),
      review: closed
        ? { state: "not_applicable", due: null }
        : !due
          ? { state: "no_review_date", due: null }
          : days < 0
            ? { state: "overdue", due, daysOverdue: -days }
            : { state: "scheduled", due, daysRemaining: days },
    };
  }

  vocabulary(): SafetyVocabulary {
    return MOCK_SAFETY_VOCABULARY;
  }

  listHazards(siteId: string): HazardPage {
    const items = this.hazards
      .filter((h) => !h.siteId || h.siteId === siteId)
      .map((h) => this.present(h))
      .sort((a, b) => b.identifiedAt.localeCompare(a.identifiedAt));
    return { items, total: this.hazards.length, truncated: false };
  }

  getHazard(id: string): HazardDetail {
    const h = this.hazard(id);
    return {
      ...this.present(h),
      assessments: h.assessments.map((a) => {
        const v = verdict(a);
        return {
          kind: a.kind,
          likelihood: a.likelihood,
          severity: a.severity,
          riskIndex: v.riskIndex ?? "",
          tolerability: v.tolerability ?? "unknown",
          matrix: v.matrix ?? "",
          rationale: a.rationale,
          assessedAt: a.at,
        };
      }),
      actions: h.actions.map((a) => ({
        ...a,
        overdue: (a.status === "open" || a.status === "in_progress") && !!a.dueDate && a.dueDate < today(),
      })),
      reports: this.reports
        .filter((r) => r.hazardId === h.id)
        .map((r) => ({ id: r.id, reference: r.reference, status: r.status, reportedAt: r.reportedAt, confidentiality: r.confidentiality })),
      acceptances: h.acceptances,
      closureNote: h.closureNote,
    };
  }

  raiseHazard(input: RaiseHazardInput, key?: string): { id: string; reference: string } {
    if (!MOCK_SAFETY_VOCABULARY.categories.some((c) => c.key === input.category)) {
      throw refuse(`no installed pack declares a hazard category '${input.category}'`);
    }
    if (!input.title.trim()) throw refuse("a hazard needs a title saying what the condition is");
    return this.once(key, () => {
      const h: StoredHazard = {
        id: crypto.randomUUID(),
        reference: nextRef("HAZ", this.hazards.map((x) => x.reference)),
        category: input.category,
        title: input.title.trim(),
        description: input.description?.trim() || undefined,
        status: "open",
        origin: input.origin ?? "report",
        siteId: input.siteId,
        area: input.area?.trim() || undefined,
        ownerLabel: input.ownerLabel?.trim() || undefined,
        identifiedAt: nowIso(),
        reviewDue: input.reviewDue || undefined,
        demo: false,
        assessments: [],
        actions: [],
        acceptances: [],
      };
      this.hazards.push(h);
      return { id: h.id, reference: h.reference };
    });
  }

  assessHazard(id: string, input: AssessHazardInput): AssessedHazard {
    const h = this.hazard(id);
    if (!LIVE_HAZARD.includes(h.status)) {
      throw refuse(`hazard ${h.reference} is ${h.status}; a score recorded against it now would not describe anything the register still holds open`);
    }
    const band = bandOf(MOCK_MATRIX, input.likelihood, input.severity);
    if (!MOCK_MATRIX.likelihood.some((l) => l.code === input.likelihood) || !MOCK_MATRIX.severity.some((s) => s.code === input.severity)) {
      throw refuse(`the matrix has no likelihood ${input.likelihood} or severity ${input.severity}`);
    }
    h.assessments.push({ kind: input.kind, likelihood: input.likelihood, severity: input.severity, at: nowIso(), rationale: input.rationale });
    const months = MOCK_MATRIX.reviewMonths[band];
    h.reviewDue = input.reviewDue || (months ? addDays(months * 30) : h.reviewDue);
    if (h.status === "open") h.status = "assessed";
    wrote();
    return {
      status: h.status,
      risk: { riskIndex: `${input.likelihood}${input.severity}`, tolerability: band },
      reviewDue: h.reviewDue,
      escalated: band === "intolerable",
      warnings: [],
    };
  }

  addHazardAction(id: string, input: HazardActionInput): { seq: number; status: string; warnings: string[] } {
    const h = this.hazard(id);
    if (!LIVE_HAZARD.includes(h.status)) throw refuse(`hazard ${h.reference} is ${h.status}; adding a mitigation to it would never be worked`);
    if (!input.description.trim()) throw refuse("a mitigating action needs a description of the control");
    if (!CONTROL_TYPES.includes(input.controlType)) throw refuse(`unknown control type '${input.controlType}'`);
    const seq = h.actions.reduce((m, a) => Math.max(m, a.seq), 0) + 1;
    h.actions.push({ seq, description: input.description.trim(), controlType: input.controlType, dueDate: input.dueDate || undefined, status: "open" });
    if (h.status === "open" || h.status === "assessed") h.status = "mitigating";
    wrote();
    return {
      seq,
      status: h.status,
      warnings: input.dueDate
        ? []
        : ["this action has no due date, so it can never be reported as overdue and will not appear in the overdue report"],
    };
  }

  completeHazardAction(id: string, seq: number, status: "done" | "cancelled"): { hazardStatus: string; warnings: string[] } {
    const h = this.hazard(id);
    const a = h.actions.find((x) => x.seq === seq && (x.status === "open" || x.status === "in_progress"));
    if (!a) throw refuse(`no open action ${seq} on this hazard; it may already be closed`);
    a.status = status;
    a.completedAt = nowIso();
    const outstanding = h.actions.some((x) => x.status === "open" || x.status === "in_progress");
    if (!outstanding && h.status === "mitigating") h.status = "monitored";
    wrote();
    return {
      hazardStatus: h.status,
      warnings:
        h.status === "monitored" && !h.reviewDue
          ? ["this hazard is now monitored and has no review date. Record a review assessment or set a review date"]
          : [],
    };
  }

  acceptHazardRisk(id: string, input: { rationale: string; reviewDue?: string }): { note: string } {
    const h = this.hazard(id);
    const last = h.assessments[h.assessments.length - 1];
    if (!last) throw refuse(`hazard ${h.reference} has never been assessed, so there is no risk to accept. Score it first`);
    if (!input.rationale.trim()) throw refuse("a risk acceptance requires a reason");
    const v = verdict(last);
    h.acceptances.push({ acceptedAt: nowIso(), rationale: input.rationale.trim(), riskIndex: v.riskIndex, tolerability: v.tolerability ?? undefined });
    if (input.reviewDue) h.reviewDue = input.reviewDue;
    wrote();
    return { note: "The risk is accepted, not removed. The hazard stays in the register at its current status and comes back round at its review date." };
  }

  closeHazard(id: string, input: CloseHazardInput): { status: string; warnings: string[] } {
    const h = this.hazard(id);
    if (!input.note.trim()) throw refuse("closing a hazard requires a note saying what changed");
    if (!LIVE_HAZARD.includes(h.status)) throw refuse(`hazard ${h.reference} is already ${h.status}`);
    const last = verdict(h.assessments[h.assessments.length - 1]);
    if (input.decision === "closed" && last.tolerability === "intolerable") {
      if (!input.acceptanceRationale?.trim()) {
        throw refuse(
          `hazard ${h.reference} is currently assessed ${last.riskIndex} — intolerable. Closing it requires an explicit acceptance of that risk, with a reason and a named person. Reassess it if the controls have reduced it; accept it if they have not`,
        );
      }
      h.acceptances.push({ acceptedAt: nowIso(), rationale: input.acceptanceRationale.trim(), riskIndex: last.riskIndex, tolerability: "intolerable" });
    }
    const warnings: string[] = [];
    if (input.decision === "closed" && last.state === "never_assessed") {
      warnings.push("this hazard was closed without ever being assessed. That is not the same thing as a hazard whose risk was found acceptable");
    }
    const open = h.actions.filter((a) => a.status === "open" || a.status === "in_progress").length;
    if (open) warnings.push(`${open} mitigating action(s) are still open. Closing the hazard does not close them`);
    h.status = input.decision;
    h.closedAt = nowIso();
    h.closureNote = input.note.trim();
    wrote();
    return { status: h.status, warnings };
  }

  /* ── reports ─────────────────────────────────────────────────────────── */

  private presentReport(r: StoredReport, redact: boolean): SafetyReport {
    const a = r.attrs;
    const text = (k: string) => (typeof a[k] === "string" && (a[k] as string).trim() ? (a[k] as string).trim() : undefined);
    const hazard = r.hazardId ? this.hazards.find((h) => h.id === r.hazardId) : undefined;
    const out: SafetyReport = {
      id: r.id,
      reference: r.reference,
      confidentiality: r.confidentiality,
      mandatory: false,
      category: r.category,
      title: r.title,
      narrative: r.narrative,
      reportedAt: r.reportedAt,
      status: r.status,
      siteId: r.siteId,
      hazardId: r.hazardId,
      hazardReference: hazard?.reference,
      outcome: r.outcome,
      closedAt: r.closedAt,
      kind: text("kind"),
      immediate: !!a.immediate,
      register: text("register"),
      where: text("where"),
      attrs: a,
      demo: r.demo,
      reporter:
        r.confidentiality === "anonymous"
          ? { state: "none", why: "this report was filed anonymously; no reporter was ever recorded" }
          : r.confidentiality === "open"
            ? { state: "named", name: r.reporter ?? undefined }
            : { state: "withheld", why: "a confidential report's reporter is not returned here" },
    };
    if (redact) {
      delete out.narrative;
      delete out.where;
      delete out.attrs;
    }
    return out;
  }

  listReports(siteId: string): SafetyReportPage {
    const items = this.reports
      .filter((r) => !r.siteId || r.siteId === siteId)
      .sort((a, b) => b.reportedAt.localeCompare(a.reportedAt))
      .map((r) => this.presentReport(r, r.confidentiality !== "open"));
    return { items, total: this.reports.length, note: "The narrative of a protected report is returned on the individual read, not in a listing." };
  }

  getReport(id: string): SafetyReport {
    const r = this.reports.find((x) => x.id === id);
    if (!r) throw refuse("no such report", 404);
    return this.presentReport(r, false);
  }

  fileReport(input: FileReportInput, key?: string): FiledReport {
    if (!CONFIDENTIALITIES.includes(input.confidentiality)) throw refuse(`unknown confidentiality '${input.confidentiality}'`);
    if (!input.narrative.trim()) throw refuse("a report needs a narrative; a title alone is a hint");
    if (input.hazardId) this.live(input.hazardId);
    return this.once(key, () => {
      const attrs: Record<string, unknown> = { ...(input.attrs ?? {}) };
      if (input.kind) attrs.kind = input.kind;
      if (input.where?.trim()) attrs.where = input.where.trim();
      if (input.immediate) attrs.immediate = true;
      const r: StoredReport = {
        id: crypto.randomUUID(),
        reference: nextRef("SR", this.reports.map((x) => x.reference)),
        confidentiality: input.confidentiality,
        title: input.title,
        narrative: input.narrative.trim(),
        category: input.category,
        siteId: input.siteId,
        reportedAt: nowIso(),
        status: "received",
        hazardId: input.hazardId,
        attrs,
        reporter: input.confidentiality === "anonymous" ? null : this.principal,
        demo: false,
      };
      this.reports.push(r);
      return {
        id: r.id,
        reference: r.reference,
        status: r.status,
        reportedAt: r.reportedAt,
        confidentiality: r.confidentiality,
        reporterRecorded: r.reporter != null,
        note: {
          open: "Your name is recorded against this report and is visible to anyone who can read reports.",
          confidential:
            "Your name is recorded and is visible only to holders of the safety report identity permission, and every time it is viewed is written to the audit log.",
          anonymous:
            "No reporter was recorded. Nobody can find out who filed this, including the platform administrator — and the safety office cannot come back to you with questions.",
        }[r.confidentiality],
      };
    });
  }

  setReportStatus(id: string, input: { status: ReportStatus; outcome?: string }): { reference: string; status: ReportStatus } {
    if (!REPORT_STATUSES.includes(input.status)) throw refuse(`unknown status '${input.status}'`);
    if ((input.status === "closed" || input.status === "rejected") && !input.outcome?.trim()) {
      throw refuse("closing a report requires an outcome. A report closed with nothing written back is what teaches people that reporting goes into a hole");
    }
    const r = this.reports.find((x) => x.id === id);
    if (!r) throw refuse("no such report", 404);
    r.status = input.status;
    if (input.outcome?.trim()) r.outcome = input.outcome.trim();
    if (input.status === "closed" || input.status === "rejected") r.closedAt = nowIso();
    wrote();
    return { reference: r.reference, status: r.status };
  }

  linkReport(id: string, hazardId: string | null): { reference: string; hazardReference: string | null } {
    const r = this.reports.find((x) => x.id === id);
    if (!r) throw refuse("no such report", 404);
    const h = hazardId ? this.live(hazardId) : null;
    r.hazardId = h?.id;
    wrote();
    return { reference: r.reference, hazardReference: h?.reference ?? null };
  }

  raiseFromReport(id: string, input: { category: string; title: string; description?: string }): { id: string; reference: string; warning?: string } {
    const r = this.reports.find((x) => x.id === id);
    if (!r) throw refuse("no such report", 404);
    if (r.hazardId) throw refuse("this report already raised a hazard; add to that one rather than splitting the trail in two");
    const raised = this.raiseHazard({ siteId: r.siteId ?? "", category: input.category, title: input.title, description: input.description });
    const h = this.hazard(raised.id);
    h.siteId = r.siteId;
    r.hazardId = h.id;
    return {
      ...raised,
      ...(r.confidentiality !== "open"
        ? { warning: "this hazard came from a protected report. Nothing of the account was copied into it" }
        : {}),
    };
  }

  /* ── security incidents ─────────────────────────────────────────────── */

  incidentCategories(): IncidentCategory[] {
    return MOCK_INCIDENT_CATEGORIES;
  }

  listIncidents(siteId: string): SecurityIncidentPage {
    const items = this.incidents
      .filter((i) => i.siteId === siteId)
      .sort((a, b) => b.detectedAt.localeCompare(a.detectedAt))
      .map(({ links: _links, siteId: _site, ...i }) => i);
    return { items, includesRestricted: true };
  }

  raiseIncident(input: RaiseIncidentInput, key?: string): RaisedIncident {
    const cat = MOCK_INCIDENT_CATEGORIES.find((c) => c.key === input.category);
    if (!cat) throw refuse(`no installed pack declares an incident category '${input.category}'`);
    const severity = input.severity ?? cat.defaultSeverity ?? 3;
    if (severity < 1 || severity > 5) throw refuse("severity must be between 1 and 5");
    return this.once(key, () => {
      const at = nowIso();
      const i = {
        id: crypto.randomUUID(),
        reference: nextRef("SEC", this.incidents.map((x) => x.reference)),
        category: cat.key,
        severity,
        status: "open" as IncidentStatus,
        classification: cat.defaultClassification,
        title: input.title,
        detectedAt: at,
        occurredAt: input.occurredAt,
        reportable: cat.reportable,
        summary: input.summary,
        detail: input.detail ?? {},
        reportedByName: this.principal,
        siteId: input.siteId,
        links: [],
      };
      this.incidents.push(i);
      return {
        id: i.id,
        reference: i.reference,
        detectedAt: at,
        ...(cat.reportable
          ? {
              notification: {
                required: true,
                to: cat.reportTo,
                withinHours: cat.reportWithinHours,
                note: "This obligation is tracked separately from the incident's status. Closing the incident does not discharge it.",
              },
            }
          : {}),
      };
    });
  }

  setIncidentStatus(id: string, input: { status: IncidentStatus; outcome?: string }): { status: IncidentStatus; warnings: string[] } {
    const i = this.incidents.find((x) => x.id === id);
    if (!i) throw refuse("no such incident", 404);
    if (!INCIDENT_TRANSITIONS[i.status].includes(input.status)) {
      const next = INCIDENT_TRANSITIONS[i.status];
      throw refuse(`an incident cannot go from ${i.status} to ${input.status}; from ${i.status} it may go to ${next.join(", ") || "nowhere"}`);
    }
    if (input.status === "closed" && !input.outcome?.trim()) {
      throw refuse("closing an incident requires an outcome. An incident closed with no statement of what was found is a gap that reads as a clean record");
    }
    i.status = input.status;
    if (input.outcome?.trim()) i.outcome = input.outcome.trim();
    if (input.status === "closed" || input.status === "cancelled") i.closedAt = nowIso();
    wrote();
    return {
      status: i.status,
      warnings:
        input.status === "closed" && i.reportable && !i.notifiedAt
          ? ["this incident is marked reportable and no notification has been recorded; closing it operationally does not discharge that"]
          : [],
    };
  }

  linkIncident(id: string, input: { subjectKind: string; subjectId: string }): void {
    const i = this.incidents.find((x) => x.id === id);
    if (!i) throw refuse("no such incident", 404);
    if (!i.links.some((l) => l.kind === input.subjectKind && l.id === input.subjectId)) {
      i.links.push({ kind: input.subjectKind, id: input.subjectId });
    }
  }
}
